import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Skeleton, Typography } from '../../../../1-primative';
import {
  Table, TableRow, TableCell, TableBody, CardContent, TableHeadCell, Card,
  Button,
} from '../../../../2-component';
import { UserContext } from '../../../../../providers/userContextProvider';
import { User } from '../../../../../interfaces';
import { formatMoneyValue, generateClientNameString } from '../../../../../util';
import { translateBackend } from '../../../../../assets/i18n/config';

const FETCH_USERS = gql`
  query searchUsers($input: UserSearchInput!, $currency: StatisticsCurrencyTypes) {
    searchUsers(input: $input) {
      users {
        id
        firstName
        lastName
        entityName
        primaryEmail
        statistics(input: { currency: $currency }) { marketValueCents }
        accounts {
          id
        }
      }
      totalCount
    }
  }
`;

const RIGHT_ALIGNED_COLS = ['totalAssets', 'organization', 'numberOfAccounts'];

export const Report = ({ options = {} }: { options?: any }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['clients']);
  const { activeOrganization, activeCurrency } = useContext(UserContext);
  const tableOptions = options.clientTable ?? [];

  const sortMap: Record<string, string> = {
    fullName: 'firstName',
    primaryEmail: 'email',
    totalAssets: 'marketValueCents',
  };
  const {
    loading, data, previousData,
  } = useQuery(FETCH_USERS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
        },
        pagination: {
          sortField: sortMap[options.clientSortBy], sortDesc: options.clientSortDirection === 'desc', perPage: 5,
        },
      },
      currency: activeCurrency || 'CAD',
    },
  });

  return (
    <Card loading={loading}>
      <Box>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='titleLarge'>{translateBackend(options.customTitle)}</Typography>
          <Button label={t('shared:viewAll')} variant='text' onClick={() => {
            navigate('/clients');
          }} />
        </CardContent>
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }} aria-label="table">
            <TableBody>
              <TableRow>
                {tableOptions.map((opt: any) => {
                  const right = RIGHT_ALIGNED_COLS.includes(opt.type);

                  return <TableHeadCell right={right}>{translateBackend(opt.label)}</TableHeadCell>;
                })}
              </TableRow>

              {loading && !previousData && [...Array(5)].map((x: any, i: number) => (
                <TableRow key={i}>
                  {tableOptions.map(() => (<TableCell><Skeleton width='100%' /></TableCell>))}
                </TableRow>
              ))}

              {(data || previousData)?.searchUsers?.users?.map((user: User) => (
                <TableRow hover key={user.id} sx={{ textDecoration: 'none', cursor: 'pointer' }} onClick={() => navigate(`/clients/${user.id}`)}>
                {tableOptions.map((opt: any) => <UserCell user={user} type={opt.type} />)}
              </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box >
    </Card>
  );
};

const UserCell = ({ user, type }: { user: User, type: string }) => {
  switch (type) {
    case 'fullName':
      return <TableCell>{generateClientNameString(user)}</TableCell>;
    case 'primaryEmail':
      return <TableCell>{user.primaryEmail}</TableCell>;
    case 'totalAssets':
      return <TableCell right>{formatMoneyValue(user.statistics?.marketValueCents)}</TableCell>;
    case 'numberOfAccounts':
      return <TableCell right>{user.accounts?.length ?? 0}</TableCell>;
    case 'organization':
      return <TableCell right>{user.organization?.name}</TableCell>;
    default:
      return <TableCell />;
  }
};
